import React from 'react';

interface ButtonGroupOption {
    value: string | number;
    label: string;
}

interface ButtonGroupProps {
    label: string;
    icon?: string;
    options: ButtonGroupOption[];
    selectedValues: (string | number)[];
    onChange: (values: (string | number)[]) => void;
    className?: string;
    disabled?: boolean;
    selectAllLabel?: string;
    clearSelectionLabel?: string;
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({
    label,
    icon,
    options,
    selectedValues,
    onChange,
    className = '',
    disabled = false,
    selectAllLabel = 'Select All',
    clearSelectionLabel = 'Deselect All'
}) => {
    const handleToggle = (value: string | number) => {
        if (disabled) {
            return;
        }

        const isSelected = selectedValues.includes(value);

        if (isSelected) {
            // Remove from selection
            onChange(selectedValues.filter((v) => v !== value));
        } else {
            // Add to selection
            onChange([...selectedValues, value]);
        }
    };

    const isAllSelected = options.length > 0 && selectedValues.length === options.length;

    const handleSelectAll = () => {
        if (disabled) {
            return;
        }

        if (isAllSelected) {
            onChange([]); // Deselect all
        } else {
            onChange(options.map((opt) => opt.value)); // Select all
        }
    };

    return (
        <div className={className}>
            <label className="form-label">
                {icon && <i className={`${icon} mr-2`} />}
                {label}
            </label>

            {/* Select All / Deselect All button */}
            <div className="mb-2">
                <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={disabled}
                    className={`btn btn-sm w-full ${isAllSelected ? 'btn-secondary' : 'btn-primary'}`}
                >
                    {isAllSelected ? clearSelectionLabel : selectAllLabel}
                </button>
            </div>

            {/* Option buttons */}
            <div className="flex flex-wrap gap-1">
                {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleToggle(option.value)}
                            disabled={disabled}
                            className={`btn btn-sm px-2 py-1 text-xs ${
                                isSelected
                                    ? 'bg-logwatch-blue text-white border-logwatch-blue'
                                    : 'bg-zinc-700 text-zinc-300 border-zinc-600 hover:bg-zinc-600'
                            }`}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>

            {/* Selection count */}
            <div className="mt-1 text-xs text-zinc-500">
                {selectedValues.length} of {options.length} selected
            </div>
        </div>
    );
};

export default ButtonGroup;
